import { Button, Dialog, Grid, Skeleton, Stack } from "@mui/material";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { useEffect, useRef, useState } from "react";
import EmailEditor, { Design } from "react-email-editor";
import AIPanel from "./AIPanel/AIPanel";
import ChooseTemplate from "./ChooseTemplate";
import { getFooterTemplate } from "screens/Templates/TemplatesService";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import SuggestionBox from "screens/Shared/SuggestionBox";
import { selectIsFirstNameMandatory } from "redux/generalSlice";
import { useAppSelector } from "utils/hooks";

interface CustomProps {
  handleDialogClose: Function;
  isDialogOpen: boolean;
  doneEditing: Function;
  design: {
    content: string;
    contentPlain: string;
    contentJson: any;
  };
  setDesign: Function;
}

const DesignEmail = (props: CustomProps) => {
  const emailEditorRef = useRef<any>();
  const [defaultFooterTemplate, setDefaultFooterTemplate] = useState<Design>();
  const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
  const [showTemplates, setShowTemplates] = useState<boolean>(false);
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);

  useEffect(() => {
    getDefaultFooterTemplate();
  }, []);

  const loadDesign = () => {
    if (emailEditorRef.current) {
      setEditorLoaded(true);
      if (isTruthy(props.design.contentJson)) {
        emailEditorRef.current.loadDesign(props.design.contentJson);
      } else {
        emailEditorRef?.current?.loadDesign(defaultFooterTemplate);
      }
    }
  };

  const handleDialogClose = () => {
    setEditorLoaded(false);
    props.handleDialogClose();
  };

  const getDefaultFooterTemplate = async () => {
    try {
      setEditorLoaded(false);
      const response = await getFooterTemplate();
      setDefaultFooterTemplate(response);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setEditorLoaded(false);
    }
  };

  const saveDesign = async () => {
    try {
      const design: any = await exportHTML();
      const plainText: any = await exportPlainText();
      // const image = await exportImage();
      await props.doneEditing({
        content: design.html,
        contentJson: design.design,
        contentPlain: plainText.text,
      });
      handleDialogClose();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const onChooseTemplate = async (jsonContent: string) => {
    if (emailEditorRef.current && jsonContent) {
      emailEditorRef?.current?.editor?.loadDesign(JSON.parse(jsonContent));
    }
    setShowTemplates(false);
  };

  const getPageHeader = () => {
    if (!editorLoaded) {
      return getSkeletonPageHeader();
    }
    return (
      <Stack p={2} spacing={2} direction="column">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack>
            <Button variant="outlined" onClick={() => handleDialogClose()}>
              Go Back
            </Button>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => setShowTemplates(true)}>
              Choose Template
            </Button>
            <Button variant="contained" onClick={saveDesign}>
              Done editing
            </Button>
          </Stack>
        </Stack>
        {isFirstNameMandatory && (
          <SuggestionBox title="$FIRST_NAME is required to enhance personalization." />
        )}
      </Stack>
    );
  };

  const getSkeletonPageHeader = () => {
    return (
      <Stack
        p={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack>
          <Skeleton animation="wave" variant="text" height={50} width={180} />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Skeleton animation="wave" variant="text" height={50} width={150} />
          <Skeleton animation="wave" variant="text" height={50} width={150} />
        </Stack>
      </Stack>
    );
  };

  const getGridSkeleton = () => {
    return (
      <Stack direction="row" spacing={3}>
        <Grid item xs={4}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Grid>
      </Stack>
    );
  };

  const getSkeletonPageBody = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} md={2}>
          <Stack spacing={1} p={2}>
            {getGridSkeleton()}
            {getGridSkeleton()}
            {getGridSkeleton()}
          </Stack>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack spacing={1} p={2}>
            <Skeleton animation="wave" variant="rounded" height={500} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={2}>
          <Stack spacing={1} p={2}>
            {getGridSkeleton()}
            {getGridSkeleton()}
            {getGridSkeleton()}
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const exportPlainText = async () => {
    return new Promise((resolve, reject) => {
      emailEditorRef.current.editor?.exportPlainText((data: any) => {
        if (data && data.text) {
          resolve(data);
        } else {
          reject(new Error("No text found in the email content."));
        }
      });
    });
  };

  const exportImage = async () => {
    return new Promise((resolve, reject) => {
      emailEditorRef.current.editor?.exportImage((data: any) => {
        if (data && data.url) {
          resolve(data);
        } else {
          reject(new Error("No text found in the email content."));
        }
      });
    });
  };

  const exportHTML = async () => {
    return new Promise((resolve, reject) => {
      emailEditorRef.current.editor?.exportHtml(
        (data: any) => {
          resolve(data);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  };

  const getEditor = () => {
    return (
      <EmailEditor
        ref={emailEditorRef}
        onReady={() => loadDesign()}
        projectId={strings.UNLAYER_PROJECT_ID}
        minHeight={isFirstNameMandatory ? 800 : 850}
        options={{
          features: {
            textEditor: {
              tables: true,
            },
          },
          appearance: {
            panels: {
              tools: {
                dock: "left",
              },
            },
          },
          customJS: [urls.customSocialTool, urls.customVideoTool],
        }}
      />
    );
  };

  const getPageBody = () => {
    return (
      <Stack direction="row">
        {getEditor()}
        <AIPanel
          exportPlainText={exportPlainText}
          exportHTML={exportHTML}
          editorLoaded={editorLoaded}
        />
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Stack spacing={1} height="100vh !important">
        {getPageHeader()}
        {getPageBody()}
      </Stack>
    );
  };

  return (
    <>
      <Dialog
        onClose={() => handleDialogClose()}
        fullWidth
        fullScreen
        open={props.isDialogOpen}
      >
        {getBody()}
      </Dialog>
      {showTemplates && (
        <ChooseTemplate
          handleDialogClose={() => setShowTemplates(false)}
          isDialogOpen={showTemplates}
          onChooseTemplate={onChooseTemplate}
        />
      )}
    </>
  );
};

export default DesignEmail;
